import { createTheme, darken } from '@mui/material/styles';

// Pulse 2.0 brand theme — sourced from requirements/ui/Shellkode Brand Guidelines.pdf
// (Color Palette + Typography sections). Extend further as more of the guide gets applied.
export const pulseTheme = createTheme({
  // Explicit, not just inherited MUI defaults — documented in packages/ui/CLAUDE.md.
  // xs: phones, sm: small tablets, md: tablets/small laptops, lg: desktop, xl: large desktop.
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 },
  },
  palette: {
    mode: 'light',
    // The brand's two named primary colors (#00275A navy, #00ADEF sky blue) plus their
    // light/dark steps from the same blue ramps in the brand palette — not invented shades.
    // primary.dark has no brand swatch (00275A is already the ramp's darkest step), so it's
    // derived via MUI's darken() rather than hand-picked.
    primary: {
      main: '#00275A',
      light: '#3971BB',
      dark: darken('#00275A', 0.25),
    },
    secondary: {
      main: '#00ADEF',
      light: '#95E2FF',
      dark: '#006DC0',
    },
    // Sourced from the brand's extended accent palette (3-shade families for graphs/
    // infographics) — gives status colors actual brand alignment instead of generic MUI
    // defaults. The palette also has yellow/purple/magenta/maroon families with no natural
    // MUI semantic slot; left out rather than inventing a custom palette extension for them.
    error: { main: '#E62C3A' },
    warning: { main: '#EF6513' },
    success: { main: '#0A8F3D' },
    info: { main: '#0A8F8F' },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
  },
  typography: {
    // Poppins is the brand's primary typeface for digital/professional use (the guide calls
    // out Satoshi for graphic-design use specifically, not app UI) — loaded in
    // apps/web/index.html. Falls back to the previous stack if it hasn't loaded yet.
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    // Sizes take the smaller end of the brand's given ranges (e.g. H1: 48-64px) rather than
    // the midpoint — the larger end would undo the compactness work already done on this
    // theme. Heading line-heights match the brand's stated 1.2x formula for larger text
    // exactly; body copy uses 1.5 rather than the brand's literal "2x for smaller text" for
    // the same compactness reason — flagged as a deliberate deviation, not an oversight.
    h1: { fontWeight: 700, fontSize: '3.5rem', lineHeight: 1.2 }, // 56px
    h2: { fontWeight: 600, fontSize: '2.25rem', lineHeight: 1.2 }, // 36px — brand spec: Semi-Bold
    h3: { fontWeight: 600, fontSize: '1.625rem', lineHeight: 1.2 }, // 26px
    h4: { fontWeight: 600, fontSize: '1.375rem', lineHeight: 1.25 }, // 22px
    h5: { fontWeight: 600, fontSize: '1.1875rem', lineHeight: 1.3 }, // 19px
    h6: { fontWeight: 600, fontSize: '1.0625rem', lineHeight: 1.3 }, // 17px
    body1: { fontSize: '1rem', lineHeight: 1.5 }, // 16px
    body2: { fontSize: '0.875rem', lineHeight: 1.5 }, // 14px
    caption: { fontSize: '0.75rem', lineHeight: 1.4 }, // 12px
  },
  components: {
    // Compact density across the app: MUI's "comfortable" defaults (the size these components
    // fall back to when no size prop is given) carry generous padding intended for sparser,
    // marketing-style UIs. A dense business app reads better with "small" everywhere — set once
    // here rather than passing size="small" at every call site.
    MuiButton: {
      defaultProps: { disableElevation: true, size: 'small' },
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 8 },
      },
    },
    MuiIconButton: {
      defaultProps: { size: 'small' },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiFormControl: {
      defaultProps: { size: 'small' },
    },
    MuiSelect: {
      defaultProps: { size: 'small' },
    },
    MuiChip: {
      defaultProps: { size: 'small' },
      styleOverrides: { root: { borderRadius: 6 } },
    },
    MuiTable: {
      defaultProps: { size: 'small' },
    },
    MuiMenuItem: {
      defaultProps: { dense: true },
    },
    // Used exclusively by NavItem/NavGroupHeader today (see packages/ui/src/components) — safe
    // to set the static sizing here rather than duplicating it as sx in both. The runtime-
    // conditional bits (justifyContent/icon width when the side nav is collapsed) stay local,
    // since the theme can't express a value that depends on a prop.
    MuiListItemButton: {
      defaultProps: { dense: true },
      styleOverrides: {
        root: {
          minHeight: 36,
          paddingTop: 4,
          paddingBottom: 4,
          marginLeft: 8,
          marginRight: 8,
          borderRadius: 8,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 36 } },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiCard: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
  },
  shape: { borderRadius: 8 },
});
